import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  formatDeclination,
  formatDegMin,
  sightReduction,
  type SightSolution,
} from "@nav-games/astro";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface SightReductionProps {
  onNavigate: (to: string) => void;
}

const MODULE = "astro";
const GAME = "sight-reduction";
const CARDS_PER_SESSION = 8;
const CHOICES_PER_CARD = 4;

interface Card {
  apLatDeg: number;
  apLonDeg: number;
  ghaDeg: number;
  decDeg: number;
  hoDeg: number;
  solution: SightSolution;
  correctStr: string;
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function fmtIntercept(nm: number, direction: "Towards" | "Away"): string {
  return `${nm.toFixed(1)} nm ${direction}`;
}

function buildCard(): Card {
  for (let attempt = 0; attempt < 60; attempt++) {
    // AP across the Med-to-Iceland band most students sail.
    const apLatDeg = randInt(35, 65);
    const apLonDeg = randInt(-20, 20);
    // Almanac-shaped numbers — random GHA, plausible Sun-style Dec.
    const ghaDeg = randInt(0, 359);
    const decDeg = randInt(-23, 23);

    const dryRun = sightReduction({ apLatDeg, apLonDeg, ghaDeg, decDeg });
    // Skip extreme geometry — Hc too low or too near zenith makes a
    // poor pedagogic example.
    if (dryRun.hcDeg < 15 || dryRun.hcDeg > 75) continue;

    // Generate a plausible Ho by offsetting Hc by a random amount in
    // tenths of an arc-minute. Keep |intercept| > 0.5 so the answer is
    // never "0.0 nm" (trivially ambiguous direction).
    const offsetMin = (randInt(-100, 100)) / 10;
    if (Math.abs(offsetMin) < 0.5) continue;

    const hoDeg = dryRun.hcDeg + offsetMin / 60;
    const sol = sightReduction({ apLatDeg, apLonDeg, ghaDeg, decDeg, hoDeg });
    if (sol.interceptNm === undefined || sol.direction === undefined) continue;

    const correctStr = fmtIntercept(sol.interceptNm, sol.direction);
    const other = sol.direction === "Towards" ? "Away" : "Towards";

    // Distractors — the four classic mistakes:
    //   • HoMoTo wrong way: same magnitude, opposite direction
    //   • magnitude doubled (forgot to divide / used minutes wrong)
    //   • magnitude halved (used Hc when Ho was given, or vice versa)
    //   • magnitude + a few, wrong direction
    const cands = [
      fmtIntercept(sol.interceptNm, other),
      fmtIntercept(sol.interceptNm * 2, sol.direction),
      fmtIntercept(Math.max(0.1, sol.interceptNm / 2), sol.direction),
      fmtIntercept(sol.interceptNm + 1.5, other),
    ];

    const seen = new Set<string>([correctStr]);
    const distractors: string[] = [];
    for (const c of cands) {
      if (distractors.length >= CHOICES_PER_CARD - 1) break;
      if (!seen.has(c)) {
        seen.add(c);
        distractors.push(c);
      }
    }
    let off = 2;
    while (distractors.length < CHOICES_PER_CARD - 1) {
      const cand = fmtIntercept(sol.interceptNm + off, sol.direction);
      if (!seen.has(cand)) {
        seen.add(cand);
        distractors.push(cand);
      }
      off += 1.5;
    }

    const choices = [correctStr, ...distractors];
    for (let i = choices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const a = choices[i]!;
      choices[i] = choices[j]!;
      choices[j] = a;
    }

    return {
      apLatDeg,
      apLonDeg,
      ghaDeg,
      decDeg,
      hoDeg,
      solution: sol,
      correctStr,
      choices,
    };
  }
  // Fallback — should essentially never hit.
  const apLatDeg = 51;
  const apLonDeg = 0;
  const ghaDeg = 45;
  const decDeg = 18;
  const dry = sightReduction({ apLatDeg, apLonDeg, ghaDeg, decDeg });
  const hoDeg = dry.hcDeg + 3 / 60;
  const sol = sightReduction({ apLatDeg, apLonDeg, ghaDeg, decDeg, hoDeg });
  const correctStr = fmtIntercept(sol.interceptNm!, sol.direction!);
  return {
    apLatDeg,
    apLonDeg,
    ghaDeg,
    decDeg,
    hoDeg,
    solution: sol,
    correctStr,
    choices: [correctStr, "3.0 nm Away", "6.0 nm Towards", "1.5 nm Towards"],
  };
}

function fmtLat(latDeg: number): string {
  return formatDeclination(latDeg);
}

function fmtLon(lonDeg: number): string {
  const hemi = lonDeg >= 0 ? "E" : "W";
  return `${formatDegMin(Math.abs(lonDeg))} ${hemi}`;
}

function fmtZn(deg: number): string {
  return `${String(Math.round(deg)).padStart(3, "0")}° T`;
}

export function SightReduction({ onNavigate }: SightReductionProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === card.correctStr;
      const attempt: Omit<Attempt, "module"> = {
        char: "sight-reduction",
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/astro")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "astro" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>What is the intercept?</h3>
        <p style={{ marginBottom: 4 }}>
          Assumed Position: <strong>{fmtLat(card.apLatDeg)}, {fmtLon(card.apLonDeg)}</strong>
        </p>
        <p style={{ marginBottom: 4 }}>
          Body: GHA <strong>{formatDegMin(card.ghaDeg)}</strong>, Dec{" "}
          <strong>{formatDeclination(card.decDeg)}</strong>
        </p>
        <p style={{ marginBottom: 12 }}>
          Observed altitude Ho: <strong>{formatDegMin(card.hoDeg)}</strong>
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === card.correctStr;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 16,
                  padding: "14px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.correctStr
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.correctStr
                ? `✓ Correct — ${card.correctStr}.`
                : `✗ Not quite — ${card.correctStr}.`}
            </p>
            <p className="small muted" style={{ margin: "8px 0 0" }}>
              LHA <strong>{formatDegMin(card.solution.lhaDeg)}</strong>;
              {" "}Hc <strong>{formatDegMin(card.solution.hcDeg)}</strong>;
              {" "}Zn <strong>{fmtZn(card.solution.znDeg)}</strong>.
              Ho − Hc ={" "}
              <strong>
                {((card.hoDeg - card.solution.hcDeg) * 60).toFixed(1)}′
              </strong>{" "}
              — HoMoTo: <strong>{card.solution.direction}</strong> the body.
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next sight →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/astro")}>
        ← Back to Astronavigation
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Astronavigation
        </button>
      </div>
    </>
  );
}
